import { Box, Typography, FormControl, InputLabel, Select, MenuItem } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useMemo, useRef, useState, useEffect } from "react"
import { Timeline, timelineItemClasses } from "@mui/lab"
import { RoadmapImplementationType, RoadmapItem, RoadmapItemType } from "../newsTypes"
import { RoadmapFeatureCard } from "./RoadmapFeatureCard"
import { RoadmapTimelineItem } from "./RoadmapTimelineItem"
import { LocalizedEnum, LocalizedStrict } from "../../localization/components/AppLocalized"
import { useActiveScrollItem } from "../useActiveScrollItem"
import { StandardNoResultText } from "../../../shared/components/noResult/StandardNoResultText"

const useStyles = makeStyles()(() => ({
    root: {
        padding: "0 1rem",
    },
    layout: {
        display: "grid",
        gridTemplateColumns: "2fr 1fr",
        gap: "1.5rem",
        alignItems: "start",
    },
    mainContent: {
        order: 1,
        overflowY: "auto",
        paddingBottom: "35rem",
        height: "100%",
    },
    timeline: {
        order: 2,
    },
    timelineContainer: {
        position: "sticky",
        top: "4rem",
        order: 2,
        maxHeight: "calc(100vh - 5rem)",
        overflowY: "auto",
    },
    selectWrap: {
        backgroundColor: "white",
        marginBottom: "1.5rem",
    },
}))

enum RoadmapFilterType {
    BUGFIX = RoadmapImplementationType.BUGFIX,
    FEATURE = RoadmapImplementationType.FEATURE,
    UPCOMING = RoadmapItemType.UPCOMING,
}

type SelectType = RoadmapFilterType | "All"

type Props = Readonly<{
    items: RoadmapItem[]
    isPublic?: boolean
}>

export function RoadmapContent({ items, isPublic }: Props) {
    const { classes } = useStyles()
    const featureRefs = useRef<Map<string, HTMLDivElement>>(new Map())
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const timelineItemRefs = useRef<Map<string, HTMLLIElement>>(new Map())
    const timelineContainerRef = useRef<HTMLDivElement>(null)
    const [roadmapType, setRoadmapType] = useState<SelectType>("All")

    const filteredItems = useMemo(() => {
        if (!isPublic) return items
        return items.filter((item) => item.roadmapDetails.implementationType !== RoadmapImplementationType.BUGFIX)
    }, [items, isPublic])

    const features = useMemo(() => {
        const getDateSource = (item: RoadmapItem) =>
            item.roadmapDetails.itemType === RoadmapItemType.IMPLEMENTED
                ? (item.roadmapDetails.implementationDate ?? item.publishDate)
                : item.publishDate

        const filtered =
            roadmapType === "All"
                ? filteredItems
                : filteredItems.filter((item) => {
                      if (roadmapType === RoadmapFilterType.UPCOMING)
                          return item.roadmapDetails.itemType === RoadmapItemType.UPCOMING
                      if (roadmapType === RoadmapFilterType.BUGFIX) {
                          return (
                              item.roadmapDetails.itemType === RoadmapItemType.IMPLEMENTED &&
                              item.roadmapDetails.implementationType === RoadmapImplementationType.BUGFIX
                          )
                      }
                      if (roadmapType === RoadmapFilterType.FEATURE) {
                          return (
                              item.roadmapDetails.itemType === RoadmapItemType.IMPLEMENTED &&
                              item.roadmapDetails.implementationType === RoadmapImplementationType.FEATURE
                          )
                      }
                      return false
                  })

        return [...filtered].sort((a, b) => getDateSource(b).localeCompare(getDateSource(a)))
    }, [filteredItems, roadmapType])

    const { activeItemId, setActiveItemId } = useActiveScrollItem(featureRefs, features)

    useEffect(() => {
        if (features.length > 0 && !activeItemId) {
            setActiveItemId(features[0].permaLink)
        }
    }, [features, activeItemId])

    // When the active item changes (driven by left-column scroll), scroll the
    // timeline panel to keep the highlighted item in view.
    // Uses manual container scroll instead of scrollIntoView to avoid bubbling to window.
    // Debounced to avoid glitching while the main scroll is still in motion.
    useEffect(() => {
        if (!activeItemId) return

        const timer = setTimeout(() => {
            const container = timelineContainerRef.current
            const el = timelineItemRefs.current.get(activeItemId)
            if (!container || !el) return

            const containerRect = container.getBoundingClientRect()
            const elRect = el.getBoundingClientRect()

            const isAbove = elRect.top < containerRect.top
            const isBelow = elRect.bottom > containerRect.bottom

            if (isAbove || isBelow) {
                const targetScrollTop =
                    container.scrollTop + elRect.top - containerRect.top - containerRect.height / 2 + elRect.height / 2
                container.scrollTo({ top: targetScrollTop, behavior: "smooth" })
            }
        }, 120)

        return () => clearTimeout(timer)
    }, [activeItemId])

    const handleTimelineClick = (permaLink: string) => {
        const element = featureRefs.current.get(permaLink)
        if (element) {
            const HEADER_OFFSET = 80
            const top = element.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET
            window.scrollTo({ top, behavior: "smooth" })
        }
    }

    if (items.length === 0) return <StandardNoResultText />

    return (
        <Box className={classes.root}>
            <FormControl sx={{ width: "10rem" }}>
                <LocalizedStrict id="news-type">
                    <InputLabel id="roadmap-type-filter-label">Type</InputLabel>
                </LocalizedStrict>
                <Select
                    labelId="roadmap-type-filter-label"
                    onChange={(e) => {
                        setRoadmapType(e.target.value as RoadmapFilterType | "All")
                    }}
                    value={roadmapType}
                    className={classes.selectWrap}
                    label="Type"
                >
                    <MenuItem value={"All"}>
                        <LocalizedStrict id="news-all">
                            <span>All</span>
                        </LocalizedStrict>
                    </MenuItem>
                    {(Object.values(RoadmapFilterType) as string[])
                        .filter((value) => !isPublic || value !== RoadmapFilterType.BUGFIX)
                        .map((value) => (
                            <MenuItem key={value} value={value}>
                                <LocalizedEnum base="news-roadmap-type-filter" enumValue={value}>
                                    <span>{value}</span>
                                </LocalizedEnum>
                            </MenuItem>
                        ))}
                </Select>
            </FormControl>
            <Box className={classes.layout}>
                <Box className={classes.mainContent} ref={scrollContainerRef}>
                    {features.map((item) => (
                        <RoadmapFeatureCard
                            key={item.permaLink}
                            item={item}
                            ref={(el) => {
                                if (el) {
                                    featureRefs.current.set(item.permaLink, el)
                                } else {
                                    featureRefs.current.delete(item.permaLink)
                                }
                            }}
                        />
                    ))}
                </Box>

                <Box className={classes.timelineContainer} ref={timelineContainerRef}>
                    <Timeline
                        className={classes.timeline}
                        position="right"
                        sx={{
                            position: "relative",
                            top: "-1rem",
                            [`& .${timelineItemClasses.root}:before`]: {
                                flex: 0,
                                padding: 0,
                            },
                        }}
                    >
                        {features.map((item, index) => (
                            <RoadmapTimelineItem
                                key={item.permaLink}
                                ref={(el) => {
                                    if (el) timelineItemRefs.current.set(item.permaLink, el)
                                    else timelineItemRefs.current.delete(item.permaLink)
                                }}
                                item={item}
                                isLast={index === features.length - 1}
                                isActive={activeItemId === item.permaLink}
                                onClick={() => handleTimelineClick(item.permaLink)}
                            />
                        ))}
                    </Timeline>
                </Box>
            </Box>
        </Box>
    )
}
